/**
 * @file <bk-quick-add>: the "Add to cart" button of a product card with a single variant
 * (snippets/quick-add.liquid). Import map name: "@theme/utils".
 *
 * Progressive enhancement: the server renders a link to the product page, so the card still works
 * with JavaScript off. When this module runs, the link acts as a button (role and Space key) and a
 * click adds the variant without leaving the page. A card holds no product form (cash-on-delivery
 * apps attach their own order button to every one), so the request is built from `data-variant-id`
 * and `data-quantity` (the variant's minimum quantity): `addToCart` posts /cart/add.js with the cart
 * drawer section, then publishes `bk:cart:update` (source "add"). The cart drawer reacts to that
 * event itself: it opens in drawer mode and shows its toast in page mode, and every
 * [data-cart-count] bubble updates. Focus is moved to the button first, so the drawer returns focus
 * there when it closes (Safari does not focus a clicked link). Modifier and middle clicks keep the
 * link: they open the product page.
 *
 * Every visible string comes from a data-* attribute rendered with `| t`: nothing is written here.
 */
import { addToCart, prefersReducedMotion } from '@theme/utils';

/** How long the button keeps its "added" tick before returning to its label. */
const ADDED_FEEDBACK_MS = 2000;

/** A Shopify variant id. Anything else leaves the link to the product page. */
const VARIANT_ID = /^[1-9]\d*$/;

class BkQuickAdd extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {AbortController | null} */
  #request = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimer;

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const button = this.#button;

    if (button) {
      button.addEventListener('click', (event) => this.#handleClick(event), { signal });

      // A link that adds to the cart is a button for assistive technology and the keyboard: Enter
      // already clicks a link; Space clicks on release, as on a native button, and never scrolls.
      if (button instanceof HTMLAnchorElement) {
        button.setAttribute('role', 'button');
        button.addEventListener('keydown', (event) => {
          if (event.key === ' ') event.preventDefault();
        }, { signal });
        button.addEventListener('keyup', (event) => {
          if (event.key !== ' ') return;
          event.preventDefault();
          button.click();
        }, { signal });
      }
    }

    // The error bubble stays until the visitor moves on: Esc, focus leaving the control, or a
    // pointer press anywhere else.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#error?.textContent) this.#setError('');
    }, { signal });
    this.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !this.contains(next)) this.#setError('');
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      const target = event.target;
      if (!(target instanceof Node) || !this.contains(target)) this.#setError('');
    }, { signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#request?.abort();
    this.#request = null;
    clearTimeout(this.#resetTimer);
  }

  /** @returns {HTMLElement | null} */
  get #button() {
    return this.querySelector('[data-quick-add-button]');
  }

  /** @returns {HTMLElement | null} */
  get #status() {
    return this.querySelector('[data-quick-add-status]');
  }

  /** @returns {HTMLElement | null} */
  get #error() {
    return this.querySelector('[data-quick-add-error]');
  }

  /** @returns {number} The quantity to add: the variant's minimum (quantity rule), at least 1 */
  get #quantity() {
    const quantity = Number.parseInt(this.dataset.quantity ?? '', 10);
    return Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
  }

  /** @param {MouseEvent} event */
  async #handleClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const variantId = this.dataset.variantId ?? '';
    if (!VARIANT_ID.test(variantId)) return;
    event.preventDefault();

    const button = this.#button;
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    button.focus({ preventScroll: true });
    this.#setError('');
    this.#request?.abort();
    this.#request = new AbortController();
    this.dataset.state = 'loading';
    button.setAttribute('aria-busy', 'true');

    const body = new FormData();
    body.set('id', variantId);
    body.set('quantity', String(this.#quantity));

    try {
      await addToCart(body, {
        sections: ['cart-drawer'],
        signal: this.#request.signal,
      });
      this.#announceAdded();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.dataset.state = '';
      const description = /** @type {{ description?: string }} */ (error)?.description;
      this.#setError(description || this.dataset.errorText || '');
    } finally {
      button.removeAttribute('aria-busy');
      this.#request = null;
    }
  }

  #announceAdded() {
    this.dataset.state = 'added';
    if (this.#status) this.#status.textContent = this.dataset.successText ?? '';

    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(
      () => {
        this.dataset.state = '';
        if (this.#status) this.#status.textContent = '';
      },
      prefersReducedMotion() ? ADDED_FEEDBACK_MS * 2 : ADDED_FEEDBACK_MS,
    );
  }

  /** @param {string} message */
  #setError(message) {
    if (this.#error) this.#error.textContent = message;
  }
}

if (!customElements.get('bk-quick-add')) customElements.define('bk-quick-add', BkQuickAdd);
